import React, { useState, useEffect } from 'react';
import {
    ArrowLeft,
    MapPin,
    Calendar,
    Clock,
    Users,
    Star,
    Share2,
    Heart,
    Copy,
    Check,
    Facebook,
    Twitter,
    Instagram,
    MessageCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiService, EventDetail } from '../services/api';
import { useEventImage } from '../hooks/useEventThumbnail';
import { goHome, goToExplore } from '../utils/navigation';
import Header from './Header';
import Footer from './Footer';
import SimpleGoogleMap from './SimpleGoogleMap';

interface EventDetailPageProps {
    eventId: number;
    onClose?: () => void;
}

const EventDetailPage: React.FC<EventDetailPageProps> = ({ eventId, onClose }) => {
    const { token } = useAuth();
    const [event, setEvent] = useState<EventDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [copyAddressSuccess, setCopyAddressSuccess] = useState(false);
    const [copyLinkSuccess, setCopyLinkSuccess] = useState(false);

    useEffect(() => {
        fetchEventDetails();
    }, [eventId, token]);

      const fetchEventDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Fetching event details for ID:', eventId);
      console.log('🔐 Using token:', token ? 'Yes' : 'No');
      
      // Try authenticated request first, fallback to public if no token
      const response = await apiService.getEventById(eventId, token || undefined);
      
      console.log('📡 Full API Response:', response);
      console.log('✅ Response success:', response.success);
      console.log('📋 Response data:', response.data);
      console.log('🎯 Response event:', response.event);
      
      // The API returns event data directly, not wrapped in success/event structure
      // Check multiple possible response structures
      let eventData: EventDetail | null = null;
      
      // Type-safe checks for the response structure
      if (response && typeof response === 'object') {
        if ('id' in response && typeof response.id === 'number') {
          // Direct event object
          eventData = response as EventDetail;
          console.log('📋 Using direct response as event data');
        } else if ('event' in response && response.event) {
          // Wrapped in event property
          eventData = response.event as EventDetail;
          console.log('📋 Using response.event as event data');
        } else if ('data' in response && response.data) {
          // Wrapped in data property
          eventData = response.data as EventDetail;
          console.log('📋 Using response.data as event data');
        }
      }
      
      console.log('🎭 Event data to use:', eventData);
      
      if (eventData && eventData.id) {
        console.log('✅ Setting event data:', eventData);
        setEvent(eventData);
      } else {
        console.log('❌ No valid event data found');
        console.log('❌ Response structure:', {
          responseType: typeof response,
          hasSuccess: 'success' in response,
          hasEvent: 'event' in response,
          hasData: 'data' in response,
          hasId: 'id' in response,
          keys: Object.keys(response)
        });
        throw new Error('No valid event data found in API response');
      }
    } catch (err) {
      console.error('💥 Error fetching event details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

    const handleBack = () => {
        if (onClose) {
            onClose();
        } else {
            goHome();
        }
    };



    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return 'Date TBA';
        }
    };

    const formatTime = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Time TBA';
        }
    };

    const formatDateRange = () => {
        if (!event?.startDate) return 'Date TBA';

        const start = new Date(event.startDate);
        const end = event.endDate ? new Date(event.endDate) : start;

        if (start.toDateString() === end.toDateString()) {
            return formatDate(event.startDate);
        } else {
            return `${formatDate(event.startDate)} - ${formatDate(event.endDate!)}`;
        }
    };

          // Component for rendering event image with thumbnail API support
    const EventImage: React.FC = () => {
        const { thumbnailUrl, loading } = useEventImage(event, eventId);
        
        if (loading) {
            return (
                <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
                    <div className="text-gray-400">Loading image...</div>
                </div>
            );
        }
        
        return (
            <img 
                src={thumbnailUrl || 'https://images.pexels.com/photos/6113659/pexels-photo-6113659.jpeg?auto=compress&cs=tinysrgb&w=800'}
                alt={event?.title || 'Event'} 
                className="w-full h-full object-contain"
                onError={(e) => {
                    console.error('❌ Image failed to load for event:', event?.title);
                    e.currentTarget.src = 'https://images.pexels.com/photos/6113659/pexels-photo-6113659.jpeg?auto=compress&cs=tinysrgb&w=800';
                }}
            />
        );
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopyLinkSuccess(true);
            setTimeout(() => setCopyLinkSuccess(false), 2000);
        } catch (error) {
            console.error('Error copying link:', error);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = window.location.href;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopyLinkSuccess(true);
                setTimeout(() => setCopyLinkSuccess(false), 2000);
            } catch (fallbackError) {
                console.error('Fallback copy failed:', fallbackError);
            }
            document.body.removeChild(textArea);
        }
    };

    const handleCopyAddress = async () => {
        const address = event?.address || event?.location || 'Address not available';
        try {
            await navigator.clipboard.writeText(address);
            setCopyAddressSuccess(true);
            // Reset the success state after 2 seconds
            setTimeout(() => setCopyAddressSuccess(false), 2000);
        } catch (error) {
            console.error('Error copying address:', error);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = address;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopyAddressSuccess(true);
                // Reset the success state after 2 seconds
                setTimeout(() => setCopyAddressSuccess(false), 2000);
            } catch (fallbackError) {
                console.error('Fallback copy failed:', fallbackError);
            }
            document.body.removeChild(textArea);
        }
    };

      const openInMaps = () => {
    if (event?.latitude && event?.longitude) {
      // Convert string coordinates to numbers for URL
      const lat = parseFloat(event.latitude);
      const lng = parseFloat(event.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        const url = `https://www.google.com/maps?q=${lat},${lng}`;
        window.open(url, '_blank');
      }
    }
  };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dpn-pink mx-auto mb-4"></div>
                    <p className="font-lucy-rose text-dpn-olive">Loading event details...</p>
                </div>
            </div>
        );
    }

    if (error || !event) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto px-4">
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        <strong className="font-bold">Error!</strong>
                        <span className="block sm:inline"> {error || 'Event not found'}</span>
                    </div>
                    <button
                        onClick={handleBack}
                        className="bg-dpn-pink text-white px-6 py-2 rounded-lg hover:bg-dpn-raspberry transition-colors duration-200"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <Header />
            <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
                {/* Header Section */}
                <section className="py-8 bg-white shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <button 
                            onClick={handleBack}
                            className="inline-flex items-center text-dpn-olive hover:text-dpn-pink transition-colors duration-200 mb-4 font-lucy-rose font-medium"
                        >
                            <ArrowLeft className="h-5 w-5 mr-2" />
                            Back to Celebrations
                        </button>
                        <h1 className="font-runtime text-3xl md:text-4xl font-bold text-black">
                            {event.title}
                        </h1>
                        <p className="font-lucy-rose text-lg text-gray-600 mt-2">
                            {event.location || 'Location TBA'}
                        </p>
                    </div>
                </section>

            {/* Hero Image Section */}
            <section className="py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="relative">
                        <div
                            className="relative aspect-[16/10] md:aspect-[21/9] overflow-hidden shadow-2xl"
                            style={{
                                borderRadius: '0.75rem', // 12px, less curve on all corners
                            }}
                        >
                            <EventImage />
                            <div
                                className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"
                                style={{
                                    borderRadius: '0.75rem',
                                }}
                            ></div>
                            {/* Info cards moved below image for all screen sizes */}
                        </div>
                        {/* Show event info cards below the image */}
                        <div className="mt-6">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 text-center">
                                    <Calendar className="h-6 w-6 text-dpn-pink mx-auto mb-2" />
                                    <p className="font-lucy-rose text-sm text-gray-600">Event Date</p>
                                    <p className="font-runtime font-bold text-black">{formatDateRange()}</p>
                                </div>
                                <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 text-center">
                                    <Users className="h-6 w-6 text-dpn-light-green mx-auto mb-2" />
                                    <p className="font-lucy-rose text-sm text-gray-600">Expected</p>
                                    <p className="font-runtime font-bold text-black">{event.expectedAttendance ? `${event.expectedAttendance.toLocaleString()}+` : 'N/A'}</p>
                                </div>
                                <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 text-center">
                                    <Clock className="h-6 w-6 text-dpn-olive mx-auto mb-2" />
                                    <p className="font-lucy-rose text-sm text-gray-600">Timing</p>
                                    <p className="font-runtime font-bold text-black">
                                        {formatTime(event.startDate)} - {event.endDate ? formatTime(event.endDate) : 'End TBA'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content Section */}
            <section className="py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* Main Content */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* About Section */}
                            <div className="bg-white rounded-2xl shadow-lg p-8">
                                <h2 className="font-runtime text-2xl font-bold text-black mb-6">About This Celebration</h2>
                                <p className="font-lucy-rose text-lg text-gray-700 leading-relaxed mb-6">
                                    {showFullDescription || event.description.length <= 300
                                        ? event.description
                                        : `${event.description.substring(0, 300)}...`}
                                </p>
                                {event.description.length > 300 && (
                                    <button
                                        onClick={() => setShowFullDescription(!showFullDescription)}
                                        className="text-dpn-pink hover:text-dpn-raspberry font-medium mb-6 font-lucy-rose"
                                    >
                                        {showFullDescription ? 'Show Less' : 'Read More'}
                                    </button>
                                )}

                                <h3 className="font-runtime text-xl font-bold text-black mb-4">Event Highlights</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {event.tags && event.tags.length > 0 ? (
                                        event.tags.map((tag, index) => (
                                            <div key={index} className="flex items-center">
                                                <Star className="h-4 w-4 text-dpn-pink mr-2 flex-shrink-0" />
                                                <span className="font-lucy-rose text-gray-700 capitalize">{tag}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <>
                                            <div className="flex items-center">
                                                <Star className="h-4 w-4 text-dpn-pink mr-2 flex-shrink-0" />
                                                <span className="font-lucy-rose text-gray-700">Traditional Pandal Decorations</span>
                                            </div>
                                            <div className="flex items-center">
                                                <Star className="h-4 w-4 text-dpn-pink mr-2 flex-shrink-0" />
                                                <span className="font-lucy-rose text-gray-700">Cultural Programs & Performances</span>
                                            </div>
                                            <div className="flex items-center">
                                                <Star className="h-4 w-4 text-dpn-pink mr-2 flex-shrink-0" />
                                                <span className="font-lucy-rose text-gray-700">Committee Feast (Bhog)</span>
                                            </div>
                                            <div className="flex items-center">
                                                <Star className="h-4 w-4 text-dpn-pink mr-2 flex-shrink-0" />
                                                <span className="font-lucy-rose text-gray-700">Photography Opportunities</span>
                                            </div>
                                            <div className="flex items-center">
                                                <Star className="h-4 w-4 text-dpn-pink mr-2 flex-shrink-0" />
                                                <span className="font-lucy-rose text-gray-700">Artisan Workshops</span>
                                            </div>
                                            <div className="flex items-center">
                                                <Star className="h-4 w-4 text-dpn-pink mr-2 flex-shrink-0" />
                                                <span className="font-lucy-rose text-gray-700">Children's Activities</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Event Details */}
                            <div className="bg-white rounded-2xl shadow-lg p-8">
                                <h2 className="font-runtime text-2xl font-bold text-black mb-6">Event Details</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <div className="flex items-start">
                                            <Calendar className="h-5 w-5 text-dpn-pink mr-3 mt-1 flex-shrink-0" />
                                            <div>
                                                <p className="font-lucy-rose font-medium text-gray-900">Event Date</p>
                                                <p className="font-lucy-rose text-gray-600">{formatDateRange()}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <Clock className="h-5 w-5 text-dpn-light-green mr-3 mt-1 flex-shrink-0" />
                                            <div>
                                                <p className="font-lucy-rose font-medium text-gray-900">Timing</p>
                                                <p className="font-lucy-rose text-gray-600">
                                                    {formatTime(event.startDate)} - {event.endDate ? formatTime(event.endDate) : 'End TBA'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <Users className="h-5 w-5 text-dpn-olive mr-3 mt-1 flex-shrink-0" />
                                            <div>
                                                <p className="font-lucy-rose font-medium text-gray-900">Expected Attendance</p>
                                                <p className="font-lucy-rose text-gray-600">{event.expectedAttendance ? `${event.expectedAttendance.toLocaleString()}+` : 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="flex items-start">
                                            <MapPin className="h-5 w-5 text-dpn-raspberry mr-3 mt-1 flex-shrink-0" />
                                            <div className="flex-1">
                                                <p className="font-lucy-rose font-medium text-gray-900">Location</p>
                                                <p className="font-lucy-rose text-gray-600 mb-2">{event.address || event.location || 'Location TBA'}</p>
                                                <button 
                                                    onClick={handleCopyAddress}
                                                    className={`inline-flex items-center font-lucy-rose text-sm font-medium transition-all duration-300 transform ${
                                                        copyAddressSuccess 
                                                            ? 'text-green-600 scale-105' 
                                                            : 'text-dpn-pink hover:text-dpn-raspberry hover:scale-105'
                                                    }`}
                                                >
                                                    {copyAddressSuccess ? (
                                                        <>
                                                            <Check className="h-4 w-4 mr-1 animate-pulse" />
                                                            Copied!
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Copy className="h-4 w-4 mr-1" />
                                                            Copy Address
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <Heart className="h-5 w-5 text-dpn-pink mr-3 mt-1 flex-shrink-0" />
                                            <div>
                                                <p className="font-lucy-rose font-medium text-gray-900">Organiser</p>
                                                <p className="font-lucy-rose text-gray-600">
                                                    {event.title}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-8">
                            {/* Map Section */}
                            {event.latitude && event.longitude && parseFloat(event.latitude) !== 0 && parseFloat(event.longitude) !== 0 && (
                                <div className="bg-white rounded-2xl shadow-lg p-8">
                                    <h3 className="font-runtime text-2xl font-bold text-black mb-6 text-center">🗺️ Find Your Way to the Celebration</h3>
                                    <div className="mb-6">
                                        <SimpleGoogleMap
                                            latitude={parseFloat(event.latitude)}
                                            longitude={parseFloat(event.longitude)}
                                            title={event.title}
                                            address={event.address || event.location || 'Event Location'}
                                            height="h-64"
                                            showFullAddress={false}
                                            enableSearch={false}
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <button
                                            onClick={openInMaps}
                                            className="w-full bg-dpn-light-green hover:bg-dpn-olive text-white font-lucy-rose font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                                        >
                                            Reach via Google Map
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Share Section */}
                            <div className="bg-white rounded-2xl shadow-lg p-8">
                                <h3 className="font-runtime text-xl font-bold text-black mb-6 text-center">Share with Friends & Family</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <button 
                                        onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                                        className="flex items-center justify-center p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
                                    >
                                        <Facebook className="h-5 w-5 mr-2" />
                                        <span className="font-lucy-rose font-medium">Facebook</span>
                                    </button>
                                    <button 
                                        onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(event?.title || 'Check out this event!')}`, '_blank')}
                                        className="flex items-center justify-center p-4 bg-sky-500 hover:bg-sky-600 text-white rounded-lg transition-colors duration-200"
                                    >
                                        <Twitter className="h-5 w-5 mr-2" />
                                        <span className="font-lucy-rose font-medium">Twitter</span>
                                    </button>
                                    <button 
                                        onClick={() => {
                                            const message = `${event?.title || 'Check out this event!'}\n\n${window.location.href}`;
                                            const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
                                            console.log('WhatsApp URL:', whatsappUrl);
                                            window.open(whatsappUrl, '_blank');
                                        }}
                                        className="flex items-center justify-center p-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200"
                                    >
                                        <MessageCircle className="h-5 w-5 mr-2" />
                                        <span className="font-lucy-rose font-medium">WhatsApp</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            // Simply open Instagram website
                                            window.open('https://www.instagram.com/', '_blank');
                                        }}
                                        className="flex items-center justify-center p-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition-colors duration-200"
                                    >
                                        <Instagram className="h-5 w-5 mr-2" />
                                        <span className="font-lucy-rose font-medium">Instagram</span>
                                    </button>
                                </div>
                                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-center gap-4">
                                        <button
                                            onClick={() => {
                                                if (navigator.share) {
                                                    navigator.share({
                                                        title: event?.title || 'Check out this event!',
                                                        text: `Check out this Durga Puja event: ${event?.title}`,
                                                        url: window.location.href
                                                    });
                                                } else {
                                                    // Fallback to copy link if native sharing is not available
                                                    handleCopyLink();
                                                }
                                            }}
                                            className="p-2 text-dpn-pink hover:text-dpn-raspberry hover:bg-pink-50 rounded-lg transition-all duration-300 transform hover:scale-110"
                                            title="Share"
                                        >
                                            <Share2 className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={handleCopyLink}
                                            className={`font-lucy-rose text-sm font-medium transition-all duration-300 transform hover:scale-105 ${
                                                copyLinkSuccess 
                                                    ? 'text-green-600 bg-green-50 px-3 py-1 rounded-lg' 
                                                    : 'text-dpn-pink hover:text-dpn-raspberry'
                                            }`}
                                        >
                                            {copyLinkSuccess ? '✓ Copied!' : 'Copy Link'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Summary Cards Section */}
            <section className="py-12 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center p-6 bg-gradient-to-br from-dpn-pink/10 to-dpn-raspberry/10 rounded-xl">
                            <Calendar className="h-8 w-8 text-dpn-pink mx-auto mb-3" />
                            <h4 className="font-runtime text-lg font-bold text-black mb-2">Duration</h4>
                            <p className="font-lucy-rose text-gray-700">5 Days of Celebration</p>
                            <p className="font-lucy-rose text-sm text-gray-600">Shashthi to Dashami</p>
                        </div>
                        <div className="text-center p-6 bg-gradient-to-br from-dpn-light-green/10 to-dpn-olive/10 rounded-xl">
                            <Users className="h-8 w-8 text-dpn-light-green mx-auto mb-3" />
                            <h4 className="font-runtime text-lg font-bold text-black mb-2">Committee</h4>
                            <p className="font-lucy-rose text-gray-700">All Welcome</p>
                            <p className="font-lucy-rose text-sm text-gray-600">Family-friendly event</p>
                        </div>
                        <div className="text-center p-6 bg-gradient-to-br from-dpn-olive/10 to-dpn-light-green/10 rounded-xl">
                            <Heart className="h-8 w-8 text-dpn-raspberry mx-auto mb-3" />
                            <h4 className="font-runtime text-lg font-bold text-black mb-2">Heritage</h4>
                            <p className="font-lucy-rose text-gray-700">UNESCO Recognized</p>
                            <p className="font-lucy-rose text-sm text-gray-600">Cultural Heritage</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action Section */}
            <section className="py-16 bg-gradient-to-r from-dpn-olive to-dpn-light-green">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-runtime text-3xl font-bold text-white mb-4">Don't Miss This Cultural Experience</h2>
                    <p className="font-lucy-rose text-xl text-white/95 mb-8">
                        Join thousands of others in celebrating this magnificent UNESCO-recognized cultural heritage
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={goToExplore}
                            className="border-2 border-white text-white hover:bg-white hover:text-dpn-olive font-lucy-rose font-semibold px-8 py-3 rounded-full transition-colors duration-200"
                        >
                            View More Events
                        </button>
                    </div>
                </div>
            </section>
        </div>
        <Footer />
        </>
    );
};

export default EventDetailPage;
